import { forwardRef, RefObject } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "src/components";
import { invoiceActions } from "src/context/invoice-slice";
import { RootState } from "src/context/store";
import FilterAccordion from "src/features/fields/container/FilterAccordion";
import { DateRange, DayPicker } from "react-day-picker";

interface FilterItem {
  id?: string;
  name?: string;
  code?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Add an index signature
}

interface EInvoiceFiltersProps {
  onClose: () => void;
  companyDetails: FilterItem[];
  divisionDetails: FilterItem[];
  cityDetails: FilterItem[];
  districtDetails: FilterItem[];
  stateDetails: FilterItem[];
  assigneeDetails: FilterItem[]; // M10-16200: e-invoice reviewer pool
  ref: RefObject<HTMLElement>;
}

const Divider = () => <div className="w-full h-[0.0625rem] my-2 bg-[#0000001a]" />;

const EInvoiceFiltersComponent = forwardRef<HTMLElement, EInvoiceFiltersProps>(
  ({ onClose, companyDetails, divisionDetails, cityDetails, districtDetails, stateDetails, assigneeDetails }, ref) => {

    const dispatch = useDispatch();

    const companyFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "company", value: details }));
    };

    const divisionFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "division", value: details }));
    };

    const statusFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "status", value: details }));
    };
    // M10-13710
    const approvalFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "isAutoApproval", value: details }));
    };
    // M10-13710
    // M10-16200
    const einvoiceCatFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "einvoiceCat", value: details }));
    };

    // M10-16200 round robin
    const assigneeFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "assignee", value: details }));
    };

    const myEinvoiceFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "myEinvoice", value: details }));
    };
    // M10-16200 round robin

    const cityFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "city", value: details }));
    };

    const districtFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "district", value: details }));
    };

    const stateFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "state", value: details }));
    };

    const dateRangeHandler = (details: DateRange | undefined) => {

      if (details?.from) {
        const fromDate = new Date(details.from);
        const toDate = details.to ? new Date(details.to) : undefined;

        const isSameDate = toDate && fromDate.getTime() === toDate.getTime();

        const payload: DateRange = {
          from: fromDate.toISOString() as any,
          ...(toDate && !isSameDate && { to: toDate.toISOString() as any }),
        };
        dispatch(invoiceActions.setEInvoiceFilters({ key: "dateRange", value: payload }));
      }
    };

    const resetFilter = () => {
      dispatch(invoiceActions.setEInvoiceFilters({ key: "company", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "division", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "status", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "city", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "district", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "state", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "dateRange", value: [] }));
      dispatch(invoiceActions.setEInvoiceFilters({ key: "isAutoApproval", value: [] }));  // M10-13710
      dispatch(invoiceActions.setEInvoiceFilters({ key: "einvoiceCat", value: [] }));  // M10-16200
      dispatch(invoiceActions.setEInvoiceFilters({ key: "assignee", value: [] }));  // M10-16200 round robin
      dispatch(invoiceActions.setEInvoiceFilters({ key: "myEinvoice", value: [] }));  // M10-16200 round robin
      dispatch(invoiceActions.setIsResetFilter(false));
    };

    const filters = useSelector((state: RootState) => state?.invoice?.EInvoiceFilters);

    return (
      <aside
        className="bg-white border-l-[1px] min-w-[24rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 text-[#49484C] text-xl font-semibold py-5 right-0  h-screen"
        ref={ref}
      >
        <div className="flex items-center justify-between gap-5 mt-3">
          <h3 className="whitespace-nowrap">Fetch filters</h3>
          <Button className="border py-1 bg-[#7F56D9] text-white" onClick={resetFilter}>
            Reset
          </Button>
          <Button className="border py-2 bg-[#7F56D9] text-white" onClick={onClose}>
            Close
          </Button>
        </div>

        {/* filters  */}
        <div className="flex flex-col mt-10">
          <FilterAccordion
            uniqueKey="code"
            displayKey="shortname"
            onSelectingFilter={companyFilterHandler}
            title="Company Name"
            arrayToFilter={companyDetails}
            defaultValue={filters.company}
            isSingleSelect={true}
          />
          <Divider />
          <FilterAccordion
            uniqueKey="company_divisioncode"
            displayKey="division_name"
            onSelectingFilter={divisionFilterHandler}
            title="Division"
            arrayToFilter={divisionDetails}
            defaultValue={filters.division}
            isSingleSelect={true}
          />
          <Divider />

          {/* <FilterAccordion
            uniqueKey="city_code"
            displayKey="city_name"
            onSelectingFilter={cityFilterHandler}
            title="City"
            arrayToFilter={cityDetails}
            defaultValue={filters.city}
            isSingleSelect={true}
          />
          <Divider /> */}
          {/* M10-13710 */}
          <FilterAccordion
            uniqueKey="id"
            displayKey="name"
            title="Auto Approved"
            arrayToFilter={[
              { id: "Yes", name: "Yes" },
              { id: "No", name: "No" },
            ]}
            onSelectingFilter={approvalFilterHandler}
            defaultValue={filters.isAutoApproval}
            isSingleSelect={true}
          />
          <Divider />
          {/* M10-13710 */}
          {/* M10-16200 */}
          <FilterAccordion
            uniqueKey="id"
            displayKey="name"
            title="E-Invoice Category"
            arrayToFilter={[
              { id: "Upload IRN/BarCode along with Invoice", name: "Category 1" },
              { id: "Upload E-Inv Aknowledgement Copy separately", name: "Category 2" },
              { id: "Periodic Bulk Upload of E-Invoice", name: "Category 3" },
            ]}
            onSelectingFilter={einvoiceCatFilterHandler}
            defaultValue={filters.einvoiceCat}
            isSingleSelect={false}
          />
          <Divider />
          {/* M10-16200 */}
          <FilterAccordion
            uniqueKey="id"
            displayKey="name"
            title="Status"
            arrayToFilter={[
              { id: "accepted", name: "Accepted" },
              { id: "rejected", name: "Rejected" },
              { id: "pending", name: "Pending" },
            ]}
            onSelectingFilter={statusFilterHandler}
            defaultValue={filters.status}
            isSingleSelect={true}
          />
          <Divider />
          {/* M10-16200 round robin */}
          <FilterAccordion
            uniqueKey="id"
            displayKey="name"
            title="My E-Invoices"
            arrayToFilter={[{ id: "Yes", name: "Only assigned to me" }]}
            onSelectingFilter={myEinvoiceFilterHandler}
            defaultValue={filters.myEinvoice}
            isSingleSelect={true}
          />
          <Divider />
          <FilterAccordion
            uniqueKey="invoice_assignment_id"
            displayKey="user_name"
            title="Assigned To"
            arrayToFilter={assigneeDetails}
            onSelectingFilter={assigneeFilterHandler}
            defaultValue={filters.assignee}
            isSingleSelect={true}
          />
          <Divider />
          {/* M10-16200 round robin */}

          <FilterAccordion
            displayKey="city_name"
            title="City"
            arrayToFilter={cityDetails}
            uniqueKey="city_code"
            onSelectingFilter={cityFilterHandler}
            defaultValue={filters.city}
            isSingleSelect={true}
          />

          <>
            <Divider />
            <FilterAccordion
              displayKey="district_name"
              title="District"
              arrayToFilter={districtDetails}
              uniqueKey="district_code"
              onSelectingFilter={districtFilterHandler}
              defaultValue={filters.district}
              isSingleSelect={true}
            />
          </>
          <>
            <Divider />
            <FilterAccordion
              displayKey="state_name"
              title="State"
              arrayToFilter={stateDetails}
              uniqueKey="state_code"
              onSelectingFilter={stateFilterHandler}
              defaultValue={filters.state}
              isSingleSelect={true}
            />
          </>
          <>
            <Divider />
            <FilterAccordion
              displayKey=""
              title="Date & Time"
              arrayToFilter={[]}
              uniqueKey=""
              onSelectingFilter={() => { }}
              defaultValue={[]}
              isSingleSelect={true}
            />
          </>
          <div className="my-4">
            {/* <DayPicker
              classNames={{
                today: `border border-[#8051FF]`,
                selected: `bg-[#ded9ff] border-[#8051FF] font-semibold`, // Highlight the selected day
                root: `border border-[#D0D0D0] rounded-xl p-5 flex items-center justify-center text-[1rem]`, // Add a shadow to the root element
                day: `p-2 cursor-pointer`, // Base styles for each day
                chevron: `fill-[#7F56D9] hover:fill-[#8051FF] transition-colors`, // Change the color of the chevron
                outside: `text-gray-400`,
              }}
              mode="range"
              defaultMonth={selected?.from}
              selected={selected}
              onSelect={setSelected}
              numberOfMonths={1}
            /> */}
            <DayPicker
              classNames={{
                today: `border border-[#8051FF]`,
                selected: `bg-[#ded9ff] border-[#8051FF] font-semibold`, // Highlight the selected day
                root: `border border-[#D0D0D0] rounded-xl p-5 flex items-center justify-center text-[1rem]`, // Add a shadow to the root element
                day: `p-2 cursor-pointer`, // Base styles for each day
                chevron: `fill-[#7F56D9] hover:fill-[#8051FF] transition-colors`, // Change the color of the chevron
                outside: `text-gray-400`,
              }}
              mode="range"
              defaultMonth={filters.dateRange?.from}
              selected={filters.dateRange}
              onSelect={dateRangeHandler}
              numberOfMonths={1}
            />
          </div>
        </div>
      </aside>
    );
  }
);

export default EInvoiceFiltersComponent;
